import { jwtDecode } from 'jwt-decode'
import { z } from 'zod'
import {
  makeTypedLocalStorageContext,
  ms,
  randomIntegerString,
  type ResourceUserInfo,
  TypedLocalStorage,
  getUserAgent,
  rnd,
} from '@w/fe-utils'
import { type MWRole, mwRoleEnum } from '@w/be-inv/db/schema/index.schema.ts'
import type { AccessTokenPayload } from '@w/be-inv/server/middleware/auth/token-types.ts'
import { conf } from '../fe-inv-conf'
import { initAuth } from '../auth/auth-init'
import { sessionSource } from '../auth/embedded-session-source'
import { isEmbeddedPage } from '../auth/embedding'

export interface UserTraits {
  id: number
  name: string
  firstName: string
  lastName: string
  email: string
  company: string
  customer_uuid: string
  companyInfo: {
    id: number
    name: string
  }
  role: MWRole
}

export interface LocalStorageSchema {
  [key: string]: unknown
  mwtoken?: string
  ajs_user_traits?: UserTraits
  invoicingAccessTokenPayload?: AccessTokenPayload // for easier user identification process for telemetry
}

export type LocalStorage = TypedLocalStorage<LocalStorageSchema>
// avoid using this directly in production
export const _typedLocalStorage = new TypedLocalStorage<LocalStorageSchema>()
const typedLocalStorageContext = makeTypedLocalStorageContext(_typedLocalStorage)
export const { useStorage } = typedLocalStorageContext
export const TypedLocalStorageProvider = typedLocalStorageContext.Provider

export const TOKEN_KEY = conf.TOKEN_KEY

const mwTokenPayloadSchema = z.object({
  exp: z.number(),
  iss: z.string().optional(),
  sub: z.string().optional(),
  tenant_id: z.array(z.string()).optional(),
  email: z.string().optional(),
  // legacy fields for backward compatibility
  user_uuid: z.string().optional(),
  company_uuid: z.string().optional(),
  user_id: z.number().optional(),
  company_id: z.number().optional(),
  role: z.enum(mwRoleEnum.enumValues).optional(),
})

export type MwTokenPayload = z.infer<typeof mwTokenPayloadSchema>

export function makeMwTokenPayload(overrides?: Partial<MwTokenPayload>): MwTokenPayload {
  const integer = randomIntegerString()
  return {
    user_uuid: randomIntegerString(),
    company_uuid: randomIntegerString(),
    email: `${overrides?.company_uuid ?? integer}-organizer@test.com`,
    exp: Date.now() + ms.day,
    ...overrides,
  }
}

export function makeUserTraits(overrides?: Partial<UserTraits>): UserTraits {
  return {
    id: rnd(1, 1000000),
    name: 'King Kong',
    firstName: 'King',
    lastName: 'Kong',
    email: 'king@kong.com',
    company: 'Meisterwerk',
    customer_uuid: randomIntegerString(),
    companyInfo: {
      id: rnd(1, 1000000),
      name: 'Meisterwerk',
    },
    role: 'organizer',
    ...overrides,
  }
}

/**
 * Extracts the company ID from the new auth-service `company_id` claim.
 * Expected format: ["tenant:<uuid>"] (we use the first occurrence).
 */
function extractCompanyIdFromClaim(companyId: unknown): string {
  if (Array.isArray(companyId) && typeof companyId[0] === 'string' && companyId[0]) {
    const raw = companyId[0]
    return raw.startsWith('tenant:') ? raw.slice('tenant:'.length) : raw
  }
  return ''
}

function _getMwToken(storage?: LocalStorage) {
  const token = (storage ?? _typedLocalStorage).getItem(TOKEN_KEY)
  if (token === undefined) {
    return undefined
  }
  if (typeof token !== 'string') {
    throw new Error(`mw token is not a string at localStorage['${TOKEN_KEY}']`)
  }
  return token
}

export const getMwTokenForForeman = _getMwToken

export function getMwTokenDataForStories(storage?: LocalStorage) {
  const token = _getMwToken(storage)
  if (!token) throw new Error('mw token is not set')
  const payload = jwtDecode<MwTokenPayload>(token) // throws error if token has invalid format
  const data = mwTokenPayloadSchema.parse(payload)
  const companyUuid = extractCompanyIdFromClaim(data.tenant_id)
  return {
    ...data,
    user_uuid: data.sub ?? '',
    company_uuid: companyUuid ?? '',
  }
}

export const getTelemetryUserData = () => _typedLocalStorage.getItem('invoicingAccessTokenPayload')

/**
 * The signed-in user's token: the SDK's, under the host's key set since INV-927, else, on a page of
 * fe-inv's own only, the one at TOKEN_KEY that tests, stories and the dev page set (`mwtoken`).
 * Deployed builds set TOKEN_KEY to the PWA's own access-token key, the token the SDK reads. Nothing
 * is refreshed or discarded here, as this runs during render.
 */
function currentAccessToken(legacyToken: string | undefined): string | undefined {
  try {
    initAuth()
    const stored = sessionSource.storedToken()
    if (stored !== null) return stored
  } catch {
    // no SDK manager to ask: only the legacy token can name the user
  }
  return isEmbeddedPage() ? undefined : legacyToken
}

export function useMwTokenDataForOnboarding() {
  // An empty default rather than none: a missing key must not throw before the SDK is asked.
  const [legacyToken] = useStorage(TOKEN_KEY, '')
  const [ajsUserTraits] = useStorage('ajs_user_traits')
  const token = currentAccessToken(typeof legacyToken === 'string' && legacyToken ? legacyToken : undefined)
  if (typeof token !== 'string')
    throw new Error(`mw token is not a string at localStorage['${TOKEN_KEY}']`)
  const payload = jwtDecode<MwTokenPayload>(token)
  const data = mwTokenPayloadSchema.parse(payload)
  const companyUuid = extractCompanyIdFromClaim(data.tenant_id)
  const userTraits = ajsUserTraits as UserTraits
  const user_id = userTraits.id
  const company_id = userTraits.companyInfo.id
  return {
    ...data,
    user_uuid: data.sub ?? '',
    company_uuid: companyUuid ?? '',
    user_id,
    company_id,
    role: userTraits.role,
  }
}

const data = getTelemetryUserData()
export const refineUserDataForTel = {
  initialData: {
    userId: String(data?.mwUserId) || '',
    entityUserId: data?.entityUserId || '',
    companyId: data?.companyId || '',
    entityId: data?.entityId || '',
    userAgent: getUserAgent(),
  },
  subscribeToUserDataChange: (cb: (data: ResourceUserInfo) => void) => {
    return _typedLocalStorage.onChange('invoicingAccessTokenPayload', (value) => {
      if (value) {
        cb({
          entityUserId: value.entityUserId,
          entityId: value.entityId,
        })
      }
    })
  },
}
